import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import FeatherIcon from 'feather-icons-react';
import { chatBitacoraIterativo } from 'src/graphql/bitacora';
import { renderBitacoraCommentWithMentions } from './renderMarkdown';
import aiIcon from 'src/assets/icons/ai-icon.svg';
import './BitacoraChatPanel.scss';

const BitacoraChatPanel = ({
  bitacora,
  onApplyReply,
  onAddAsComment,
  onClose,
  className = '',
}) => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initialized, setInitialized] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (!isExpanded) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setIsExpanded(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isExpanded]);

  useEffect(() => {
    if (!bitacora?.id || initialized) return;

    const initChat = async () => {
      setLoading(true);
      setError('');
      try {
        const { reply } = await chatBitacoraIterativo(bitacora.id, []);
        setMessages([{ role: 'assistant', content: reply }]);
        setInitialized(true);
      } catch (err) {
        setError(err?.message || 'No se pudo iniciar el chat.');
      } finally {
        setLoading(false);
      }
    };

    initChat();
  }, [bitacora?.id, initialized]);

  const handleSend = async () => {
    const text = (inputValue || '').trim();
    if (!text || loading || !bitacora?.id) return;

    const userMsg = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setLoading(true);
    setError('');

    try {
      const nextHistory = [...messages, userMsg].map((m) => ({
        role: m.role,
        content: m.content,
      }));
      const { reply } = await chatBitacoraIterativo(bitacora.id, nextHistory);
      setMessages((prev) => [...prev, { role: 'assistant', content: reply }]);
    } catch (err) {
      setError(err?.message || 'No se pudo obtener respuesta.');
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const lastAssistantMessage = messages.filter((m) => m.role === 'assistant').pop();
  const canApply = lastAssistantMessage?.content && onApplyReply;
  const canAddComment = lastAssistantMessage?.content && onAddAsComment;

  const panel = (
    <div className={`bitacora-chat-panel ${isExpanded ? 'bitacora-chat-panel--expanded' : ''} ${className}`}>
      <div className="bitacora-chat-panel-header">
        <div className="bitacora-chat-panel-header-title">
          <span>Chat con IA</span>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          className="bitacora-chat-panel-expand"
          title={isExpanded ? 'Salir de pantalla completa' : 'Abrir en pantalla completa'}
        >
          <FeatherIcon icon={isExpanded ? 'minimize-2' : 'maximize-2'} size={16} strokeWidth={2} />
        </button>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="bitacora-chat-panel-close"
            title="Cerrar chat"
          >
            <FeatherIcon icon="x" size={18} strokeWidth={2} />
          </button>
        )}
      </div>

      <div ref={messagesContainerRef} className="bitacora-chat-panel-messages">
        {loading && !initialized ? (
          <div className="bitacora-chat-panel-loading">
            <div className="bitacora-chat-panel-loading-avatar">
              <img src={aiIcon} alt="" aria-hidden />
            </div>
            <div className="bitacora-chat-panel-loading-dots">
              <span className="bitacora-chat-panel-loading-dot" />
              <span className="bitacora-chat-panel-loading-dot" />
              <span className="bitacora-chat-panel-loading-dot" />
            </div>
          </div>
        ) : error && messages.length === 0 ? (
          <div className="bitacora-chat-panel-error">{error}</div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              className={`bitacora-chat-panel-message bitacora-chat-panel-message--${msg.role}`}
            >
              {msg.role === 'assistant' && (
                <div className="bitacora-chat-panel-message-avatar">
                  <img src={aiIcon} alt="" aria-hidden />
                </div>
              )}
              <div className="bitacora-chat-panel-message-content">
                {msg.role === 'user' ? (
                  <p>{msg.content}</p>
                ) : (
                  <div
                    className="bitacora-chat-panel-message-markdown"
                    dangerouslySetInnerHTML={{
                      __html: renderBitacoraCommentWithMentions(msg.content),
                    }}
                  />
                )}
              </div>
            </div>
          ))
        )}
        {loading && initialized && (
          <div className="bitacora-chat-panel-message bitacora-chat-panel-message--assistant">
            <div className="bitacora-chat-panel-message-avatar">
              <img src={aiIcon} alt="" aria-hidden />
            </div>
            <div className="bitacora-chat-panel-typing">
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>

      {error && messages.length > 0 && (
        <div className="bitacora-chat-panel-error-inline">{error}</div>
      )}

      <div className="bitacora-chat-panel-input-wrap">
        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe para iterar sobre el análisis…"
          disabled={loading || !initialized}
          rows={2}
          className="bitacora-chat-panel-input"
        />
        <div className="bitacora-chat-panel-input-actions">
          {canApply && (
            <button
              type="button"
              className="bitacora-chat-panel-apply-btn"
              onClick={(e) => {
                e.stopPropagation();
                onApplyReply(lastAssistantMessage.content);
              }}
            >
              <img src={aiIcon} alt="" className="bitacora-chat-panel-apply-btn__icon" aria-hidden />
              <span>Reemplazar análisis</span>
            </button>
          )}
          {canAddComment && (
            <button
              type="button"
              className="bitacora-chat-panel-add-comment-btn"
              onClick={(e) => {
                e.stopPropagation();
                onAddAsComment(lastAssistantMessage.content);
              }}
            >
              <span>Añadir comentario</span>
            </button>
          )}
          <button
            type="button"
            className="bitacora-chat-panel-send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() || loading || !initialized}
            title="Enviar"
          >
            <FeatherIcon icon="send" size={18} strokeWidth={2} />
          </button>
        </div>
      </div>
    </div>
  );

  if (isExpanded && typeof document !== 'undefined') {
    return createPortal(
      <div className="bitacora-chat-overlay">
        {panel}
      </div>,
      document.body,
    );
  }

  return panel;
};

export default BitacoraChatPanel;
